import React, { useState, useEffect, useCallback } from 'react';
import ToolDescription from '../common/toolDescription';
import CscBillTab from './cscBillTab';
import CscProposalTab from './cscProposalTab';
import CscCombinedTab from './cscCombinedTab';
import { buildCscWhatsAppMsg } from './cscWhatsApp';
import useRscPricing, { commercialPricePerKwp, defaultBrandForKwp, availableBrandsForKwp, batteryPricePerKwh } from '../../hooks/useRscPricing';
import {
    Box, Typography, TextField, Select, MenuItem, FormControl,
    Button, Card, Chip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';

const PP = "'Poppins', sans-serif";

const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF',
    bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', disabled: '#94A3B8',
    border: '#E2E8F0', success: '#10B981', warning: '#F59E0B', error: '#EF4444',
};

const labelSx = { fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP };
const sectionSx = {
    fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: `1px solid ${COLORS.border}`, pb: 0.5, mb: 1.5, fontFamily: PP,
};
const fieldSx = { size: 'small', fullWidth: true, InputProps: { sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } } };

const Label = ({ children, required }) => (
    <Typography sx={labelSx}>
        {children}{required && <span style={{ color: COLORS.error }}> *</span>}
    </Typography>
);

const SectionTitle = ({ children }) => <Typography sx={sectionSx}>{children}</Typography>;

const ModeToggle = ({ value, onChange, options, compact = false }) => (
    // compact = RSC-style small left-aligned buttons (e.g. the language picker);
    // default = full-width (the in-form toggles).
    <ToggleButtonGroup exclusive fullWidth={!compact} value={value} onChange={(e, v) => v && onChange(v)}
        sx={{ mb: 1.5, ...(compact ? { width: 'fit-content' } : {}),
              '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', py: 0.6, px: compact ? 1.75 : undefined, borderColor: COLORS.border },
              '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
        {options.map(([val, label]) => <ToggleButton key={val} value={val}>{label}</ToggleButton>)}
    </ToggleButtonGroup>
);

const RMField = ({ value, onChange, placeholder = '0' }) => (
    <TextField {...fieldSx} type="number" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
        InputProps={{ ...fieldSx.InputProps, startAdornment: <span style={{ color: COLORS.primary, fontWeight: 700, fontFamily: PP, marginRight: 4, fontSize: '0.85rem' }}>RM</span> }} />
);

const PctField = ({ value, onChange, placeholder = '0' }) => (
    <TextField {...fieldSx} type="number" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
        InputProps={{ ...fieldSx.InputProps, startAdornment: <span style={{ color: COLORS.primary, fontWeight: 700, fontFamily: PP, marginRight: 4, fontSize: '0.85rem' }}>%</span> }} />
);

export default function CommercialSalesCalculator() {

    const [formData, setFormData] = useState({
        companyName: '', panelWatt: '640', panelWattCustom: '',
        panels: '', kwp: '', kw: '', battKwh: '',
        tariffType: '', monthlyBill: '',
        pricePerKwp: '', totalSolarPrice: '', battCost: '',
        discPct: '', discAmt: '',
        savingRedPct: '', savingRedAmt: '',
        sareRate: '',
        inverterBrand: 'goodwe',
    });

    const [activeTab,   setActiveTab]   = useState('combined');
    const [priceMode,   setPriceMode]   = useState('kwp');
    const { atapPrices } = useRscPricing('Residential Pricing', 'single', 999);
    const [residentialAutoFilled, setResidentialAutoFilled] = useState(false);
    const [phaseMode,   setPhaseMode]   = useState('3phase');   // default 3-phase (commercial is almost always 3-phase)
    const [discMode,    setDiscMode]    = useState('none');
    const [savingMode,  setSavingMode]  = useState('none');
    const [genMode,     setGenMode]     = useState('std');
    const [currentLang, setCurrentLang] = useState('en');
    const [calcResult,  setCalcResult]  = useState(null);

    const [showCustomWatt, setShowCustomWatt] = useState(false);
    const [kwpHint,        setKwpHint]        = useState('');
    const [billHint,       setBillHint]       = useState('');
    const [showBreakdown,  setShowBreakdown]  = useState(false);
    const [breakdown,      setBreakdown]      = useState({});
    const [copyText,       setCopyText]       = useState('📋 Copy Message');
    const [isCopied,       setIsCopied]       = useState(false);

    const TARIFF_LABELS = { '0.515': 'Low Voltage', '0.303': 'Medium Voltage', '0.437': 'High Voltage', '0.451': 'Agriculture' };

    const MAINT_TIERS = [
        { max: 6.82, rate: 1800 },  { max: 10.54, rate: 2300 }, { max: 20,   rate: 2600 },
        { max: 40,   rate: 3500 },  { max: 60,    rate: 4500 }, { max: 80,   rate: 4500 },
        { max: 100,  rate: 4500 },  { max: 300,   rate: 5000 }, { max: 600,  rate: 7500 },
        { max: 1000, rate: 11000 },
    ];

    const fmt   = n => Number(n).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const fmtRM = n => 'RM ' + fmt(n);

    const getMaintenance = kwp => { for (const t of MAINT_TIERS) if (kwp <= t.max) return t.rate; return 11000; };
    const getTaxRelief   = (kwp, cap) => ({ relief: cap * (kwp < 60 ? 0.24 : 0.388), pct: kwp < 60 ? 24 : 38.8 });
    const getWp          = (fd) => fd.panelWatt === 'other' ? (parseFloat(fd.panelWattCustom) || 0) : (parseFloat(fd.panelWatt) || 620);
    const set            = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    // Unified inverter sizing rule (per spec, 2026-05-20):
    //   ≤26 kWp   → look up ATAP Residential Pricing row (phase-specific
    //               inverterSize / inverterSize3p); fall back to
    //               Math.ceil(kwp/1.35) if no ATAP row matches
    //   ~96 kWp   → 72 kW (known product spec: 150 × 640W → GoodWe 72K)
    //   otherwise → smallest 5-kW multiple where kWp/kW < 1.35
    // Mirrors autoInverterKw in cscProposalTab.jsx — keep both in sync.
    const autoFillInverter = useCallback(kwp => {
        const k = Number(kwp) || 0;
        if (k <= 0) return null;
        if (k <= 26) {
            const match = (atapPrices || []).find(p => Math.abs(p.kwp - k) < 0.05);
            if (match) {
                const str = phaseMode === '3phase' ? match.inverter3p : match.inverter;
                const m = /(\d+(?:\.\d+)?)\s*k/i.exec(String(str || ''));
                if (m) return Number(m[1]);
            }
            return Math.ceil(k / 1.35);
        }
        if (k > 95 && k <= 96.1) return 72;
        return Math.ceil(k / 1.35 / 5) * 5;
    }, [atapPrices, phaseMode]);

    const autoFillSareRate = useCallback(kwp => {
        if (!kwp || kwp <= 0) return null;
        if (kwp <= 100) return 0.36;
        if (kwp < 300)  return 0.32;
        if (kwp < 600)  return 0.30;
        return 0.28;
    }, []);

    const getSolarPrice = useCallback((fd, pm) => {
        const _fd = fd || formData;
        const _pm = pm || priceMode;
        const kwp = parseFloat(_fd.kwp) || 0;
        if (_pm === 'total') {
            const total = parseFloat(_fd.totalSolarPrice) || 0;
            return { solarCost: total, ppk: kwp > 0 && total > 0 ? total / kwp : 0 };
        }
        const ppk = parseFloat(_fd.pricePerKwp) || 0;
        return { solarCost: kwp * ppk, ppk };
    }, [formData, priceMode]);

    const getDiscount = useCallback((cap, fd, dm) => {
        const _fd = fd || formData;
        const _dm = dm || discMode;
        if (_dm === 'pct') return cap * ((parseFloat(_fd.discPct) || 0) / 100);
        if (_dm === 'amt') return parseFloat(_fd.discAmt) || 0;
        return 0;
    }, [formData, discMode]);

    const getSavingReduction = useCallback((savingMo, fd, sm) => {
        const _fd = fd || formData;
        const _sm = sm || savingMode;
        if (_sm === 'pct') return savingMo * ((parseFloat(_fd.savingRedPct) || 0) / 100);
        if (_sm === 'amt') return parseFloat(_fd.savingRedAmt) || 0;
        return 0;
    }, [formData, savingMode]);

    const onKwpManual = value => {
        const kwp = parseFloat(value) || 0;
        const kw  = autoFillInverter(kwp);
        const sr  = genMode === 'sare' ? autoFillSareRate(kwp) : null;
        setFormData(prev => ({
            ...prev, kwp: value,
            kw: kw !== null ? kw.toString() : prev.kw,
            ...(sr !== null ? { sareRate: sr.toString() } : {}),
        }));
    };

    // Cascade panels → kWp
    useEffect(() => {
        const p  = parseFloat(formData.panels) || 0;
        const wp = getWp(formData);
        if (p > 0 && wp > 0) {
            const kwp = +(p * wp / 1000).toFixed(2);
            const kw  = autoFillInverter(kwp);
            const sr  = genMode === 'sare' ? autoFillSareRate(kwp) : null;
            setKwpHint(`Auto: ${p} × ${wp}Wp = ${kwp}kWp`);
            setFormData(prev => ({
                ...prev, kwp: kwp.toString(),
                kw: kw !== null ? kw.toString() : prev.kw,
                ...(sr !== null ? { sareRate: sr.toString() } : {}),
            }));
        }
    }, [formData.panels, formData.panelWatt, formData.panelWattCustom]);

    // Bill hint
    useEffect(() => {
        const bill = parseFloat(formData.monthlyBill) || 0;
        const rate = parseFloat(formData.tariffType)  || 0;
        setBillHint(bill > 0 && rate > 0 ? `⚡ Est. consumption: ${fmt(Math.round(bill / rate))} kWh/month` : '');
    }, [formData.monthlyBill, formData.tariffType]);

    // Auto-fill price based on kWp tier + inverter brand.
    //   <26 kWp  → Residential Pricing per phase mode (resi as-is).
    //   ≥26 kWp  → Commercial brand tier (GoodWe/Huawei) × inverter type.
    // Re-runs whenever kWp / panels / brand / phase changes.
    useEffect(() => {
        const panels = parseInt(formData.panels, 10);
        const kwp = parseFloat(formData.kwp) || 0;
        const brand = formData.inverterBrand || 'goodwe';
        if (kwp <= 0) {
            setResidentialAutoFilled(false);
            return;
        }
        let perKwp = 0;
        let usedResi = false;
        const invType = (parseFloat(formData.battKwh) > 0) ? 'hybrid' : 'string';
        if (kwp < 26) {
            // Need a Residential Pricing row matching panel count to pull resi RM/kWp
            if (!panels || !atapPrices?.length) {
                setResidentialAutoFilled(false);
                return;
            }
            const match = atapPrices.find(r => r.panels === panels);
            if (!match) {
                setResidentialAutoFilled(false);
                return;
            }
            const primary = phaseMode === '3phase' ? match.cash3p : match.cash;
            const secondary = phaseMode === '3phase' ? match.cash : match.cash3p;
            const totalResi = primary > 0 ? primary : secondary;
            if (!totalResi) {
                setResidentialAutoFilled(false);
                return;
            }
            const resiPerKwp = totalResi / match.kwp;
            perKwp = commercialPricePerKwp(kwp, brand, invType, resiPerKwp);
            usedResi = true;
        } else {
            perKwp = commercialPricePerKwp(kwp, brand, invType, 0);
        }
        if (!perKwp) {
            setResidentialAutoFilled(false);
            return;
        }
        const total = Math.round(perKwp * kwp);
        setFormData(prev => ({
            ...prev,
            totalSolarPrice: String(total),
            pricePerKwp: String(Math.round(perKwp)),
        }));
        setResidentialAutoFilled(usedResi);
    }, [formData.panels, formData.kwp, formData.inverterBrand, formData.battKwh, atapPrices, phaseMode]);

    // Force-switch brand when kWp moves out of the current brand's range
    // (e.g. user types <100 kWp while Huawei selected → flip to GoodWe).
    useEffect(() => {
        const kwp = parseFloat(formData.kwp) || 0;
        if (kwp <= 0) return;
        const allowed = availableBrandsForKwp(kwp);
        if (!allowed.includes(formData.inverterBrand)) {
            setFormData(prev => ({ ...prev, inverterBrand: defaultBrandForKwp(kwp) }));
        }
    }, [formData.kwp, formData.inverterBrand]);

    // Auto-fill battery add-on cost from kWh × tier rate when battKwh changes
    // (only if user hasn't manually overridden battCost).
    const [battCostTouched, setBattCostTouched] = useState(false);
    useEffect(() => {
        if (battCostTouched) return;
        const kwh = parseFloat(formData.battKwh) || 0;
        if (kwh <= 0) {
            setFormData(prev => ({ ...prev, battCost: '' }));
            return;
        }
        const cost = Math.round(kwh * batteryPricePerKwh(kwh));
        setFormData(prev => ({ ...prev, battCost: String(cost) }));
    }, [formData.battKwh, battCostTouched]);

    // Live breakdown
    useEffect(() => {
        const kwp = parseFloat(formData.kwp) || 0;
        const bc  = parseFloat(formData.battCost) || 0;
        const { solarCost } = getSolarPrice(formData, priceMode);
        if (kwp > 0 && solarCost > 0) {
            const raw   = solarCost + bc;
            const disc  = getDiscount(raw, formData, discMode);
            const net   = raw - disc;
            const maint = getMaintenance(kwp);
            const { relief, pct } = getTaxRelief(kwp, net);
            setBreakdown({
                solar:    fmtRM(Math.round(solarCost)),
                battery:  bc > 0 ? fmtRM(bc) : '—',
                total:    disc > 0 ? `${fmtRM(Math.round(raw))} → ${fmtRM(Math.round(net))}` : fmtRM(Math.round(raw)),
                maint:    fmtRM(maint) + '/yr',
                tax:      fmtRM(Math.round(relief)) + ` (${pct}%)`,
                afterTax: fmtRM(Math.round(net - relief)),
            });
            setShowBreakdown(true);
        } else {
            setShowBreakdown(false);
        }
    }, [formData.kwp, formData.pricePerKwp, formData.totalSolarPrice, formData.battCost,
        formData.discPct, formData.discAmt, priceMode, discMode]);

    const handlePriceModeChange = mode => {
        setPriceMode(mode);
        setFormData(prev => ({ ...prev, ...(mode === 'kwp' ? { totalSolarPrice: '' } : { pricePerKwp: '' }) }));
    };

    const handleGenModeChange = mode => {
        setGenMode(mode);
        if (mode === 'sare') {
            const kwp = parseFloat(formData.kwp) || 0;
            const sr  = autoFillSareRate(kwp);
            if (sr !== null) set('sareRate', sr.toString());
        }
    };

    const calculate = () => {
        const kwp      = parseFloat(formData.kwp)       || 0;
        const panels   = parseFloat(formData.panels)    || 0;
        const kw       = parseFloat(formData.kw)        || 0;
        const battKwh  = parseFloat(formData.battKwh)   || 0;
        const battCost = parseFloat(formData.battCost)  || 0;
        const rate     = parseFloat(formData.tariffType) || 0;
        const bill     = parseFloat(formData.monthlyBill) || 0;
        const company  = formData.companyName;
        const tariffLabel = TARIFF_LABELS[formData.tariffType] || '';
        const { solarCost, ppk } = getSolarPrice();

        if (!kwp || !solarCost || !rate) { alert('Please fill in: kWp, Solar Price, and Tariff Type.'); return; }

        const capitalCostRaw = solarCost + battCost;
        const discount       = getDiscount(capitalCostRaw);
        const capitalCost    = capitalCostRaw - discount;
        const maintenance    = getMaintenance(kwp);
        const { relief, pct } = getTaxRelief(kwp, capitalCost);
        const afterTaxCost   = capitalCost - relief;

        const sareRate    = genMode === 'sare' ? (parseFloat(formData.sareRate) || 0) : 0;
        const effRate     = genMode === 'sare' ? Math.max(rate - sareRate, 0) : rate;
        const savingMoRaw = kwp * 3.4 * 30 * effRate;
        const reduction   = getSavingReduction(savingMoRaw);
        const savingMo    = Math.max(savingMoRaw - reduction, 0);
        const savingYr    = savingMo * 12;
        const saving10    = savingYr * 10;
        const saving20    = savingYr * 20;

        const roi           = capitalCost / savingYr;
        const roiAfterTax   = afterTaxCost / savingYr;
        const yieldRate     = (1 / roi) * 100;
        const yieldAfterTax = (1 / roiAfterTax) * 100;
        const billSavingPct = bill > 0 ? Math.min((savingMo / bill) * 100, 100) : null;

        setCalcResult({
            kwp, panels, kw, battKwh,
            capitalCost, capitalCostRaw, discount, discMode,
            maintenance, relief, afterTaxCost, taxPct: pct,
            ppk, battCost, rate, tariffLabel, bill, billSavingPct, company,
            genMode, sareRate, savingMode, reduction,
            savingMo, savingYr, saving10, saving20,
            roi, roiAfterTax, yieldRate, yieldAfterTax,
        });
    };

    const copyMsg = async () => {
        if (!calcResult) return;
        const msg = buildCscWhatsAppMsg(calcResult, currentLang);
        try { await navigator.clipboard.writeText(msg); } catch {
            const ta = document.createElement('textarea');
            ta.value = msg; ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta);
        }
        setCopyText('✅ Copied!'); setIsCopied(true);
        setTimeout(() => { setCopyText('📋 Copy Message'); setIsCopied(false); }, 2200);
    };

    const openWA = async () => {
        if (!calcResult) return;
        const msg      = buildCscWhatsAppMsg(calcResult, currentLang);
        const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
        if (isMobile) { window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank'); }
        else {
            try { await navigator.clipboard.writeText(msg); } catch {}
            window.open('https://web.whatsapp.com/', '_blank');
            setCopyText('✅ Copied — paste in WA'); setIsCopied(true);
            setTimeout(() => { setCopyText('📋 Copy Message'); setIsCopied(false); }, 3500);
        }
    };

    const BRow = ({ label, value, color }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.sub, fontFamily: PP }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: color || COLORS.text, fontFamily: PP }}>{value}</Typography>
        </Box>
    );

    return (
        <Box sx={{ backgroundColor: COLORS.bg }}>
            {/* Header — RSC-style: full-width, LEFT-aligned, compact language toggle
                stacked below the title (not centered, not full-width). */}
            <Box sx={{ px: 2, pt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                        {currentLang === 'zh' ? '商业太阳能计算器' : currentLang === 'ms' ? 'Kalkulator Solar Komersial' : 'Commercial Solar Calculator'}
                        <Typography component="span" sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP, ml: 1, fontWeight: 400 }}>Updated 2 Jun 2026</Typography>
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP }}>
                        {currentLang === 'zh' ? '计算 · 提案 · 表格' : currentLang === 'ms' ? 'Kalkulator · Cadangan · Borang' : 'Calculator · Proposal · Forms'}
                    </Typography>
                </Box>
                <ModeToggle compact value={currentLang} onChange={setCurrentLang} options={[['en', '🇬🇧 EN'], ['zh', '🇨🇳 中文'], ['ms', '🇲🇾 BM']]} />
            </Box>

            {/* Temporary tab switch — System tab re-opened on request (alongside the Calculator). */}
            <Box sx={{ px: 2, pt: 1.5, display: 'flex', gap: 1 }}>
                {[['combined', currentLang === 'zh' ? '计算器' : currentLang === 'ms' ? 'Kalkulator' : 'Calculator'],
                  ['sizing', currentLang === 'zh' ? '系统' : 'System']].map(([key, label]) => (
                    <Button key={key} onClick={() => setActiveTab(key)} variant={activeTab === key ? 'contained' : 'outlined'} size="small"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
                        {label}
                    </Button>
                ))}
            </Box>

            {activeTab === 'bill' && <CscBillTab />}

            {activeTab === 'combined' && <CscCombinedTab sharedPhaseMode={phaseMode} setSharedPhaseMode={setPhaseMode} lang={currentLang} />}

            {activeTab === 'proposal' && <CscProposalTab sharedForm={formData} atapPrices={atapPrices} sharedPhaseMode={phaseMode} />}

            {activeTab === 'sizing' && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 0, md: 0 } }}>

                {/* ── Left Sidebar ── */}
                <Box sx={{ width: { xs: '100%', md: 340 }, minWidth: { xs: 'auto', md: 340 }, backgroundColor: COLORS.card, borderRight: { xs: 'none', md: `1px solid ${COLORS.border}` }, borderBottom: { xs: `1px solid ${COLORS.border}`, md: 'none' }, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Company */}
                    <Box>
                        <SectionTitle>🏢 Company (Optional)</SectionTitle>
                        <TextField {...fieldSx} placeholder="e.g. Frontline Engineering" value={formData.companyName} onChange={e => set('companyName', e.target.value)} />
                    </Box>

                    {/* Language */}
                    <Box>
                        <SectionTitle>🌐 WhatsApp Language</SectionTitle>
                        <ModeToggle value={currentLang} onChange={setCurrentLang} options={[['en','🇬🇧 EN'],['zh','🇨🇳 中文'],['ms','🇲🇾 BM']]} />
                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP }}>Language for WhatsApp export.</Typography>
                    </Box>

                    {/* System Specs */}
                    <Box>
                        <SectionTitle>⚙️ System Specifications</SectionTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box>
                                <Label>Phase <span style={{ color: COLORS.sub, fontWeight: 400 }}>(for &lt;26 kWp auto-pricing)</span></Label>
                                <ModeToggle value={phaseMode} onChange={setPhaseMode} options={[['single','⚡ Single Phase'],['3phase','⚡⚡⚡ 3 Phase']]} />
                            </Box>
                            <Box>
                                <Label>Inverter Brand <span style={{ color: COLORS.sub, fontWeight: 400 }}>(drives commercial RM/kWp)</span></Label>
                                <ModeToggle value={formData.inverterBrand}
                                    onChange={v => set('inverterBrand', v)}
                                    options={(() => {
                                        const kwpNow = parseFloat(formData.kwp) || 0;
                                        const allowed = kwpNow > 0 ? availableBrandsForKwp(kwpNow) : ['goodwe','huawei'];
                                        const all = [['goodwe','GoodWe'],['huawei','Huawei']];
                                        return all.filter(([k]) => allowed.includes(k));
                                    })()} />
                                <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                    GoodWe: all sizes (String/Hybrid) · Huawei: 100 kWp and above (String only)
                                </Typography>
                            </Box>
                            <Box>
                                <Label>Panel Wattage (Wp)</Label>
                                <FormControl size="small" fullWidth>
                                    <Select value={formData.panelWatt}
                                        onChange={e => { setShowCustomWatt(e.target.value === 'other'); set('panelWatt', e.target.value); }}
                                        sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' }}>
                                        {['620','640'].map(w => <MenuItem key={w} value={w} sx={{ fontFamily: PP }}>{w} Wp</MenuItem>)}
                                        <MenuItem value="other" sx={{ fontFamily: PP }}>Other — enter manually</MenuItem>
                                    </Select>
                                </FormControl>
                                {showCustomWatt && (
                                    <TextField {...fieldSx} type="number" placeholder="Enter Wp" value={formData.panelWattCustom}
                                        onChange={e => set('panelWattCustom', e.target.value)} sx={{ mt: 1 }} />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Label>No. of Panels</Label>
                                    <TextField {...fieldSx} type="number" placeholder="48" value={formData.panels} onChange={e => set('panels', e.target.value)} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Label>System Size (kWp)</Label>
                                    <TextField {...fieldSx} type="number" placeholder="auto" step="0.01" value={formData.kwp} onChange={e => onKwpManual(e.target.value)} />
                                </Box>
                            </Box>
                            {kwpHint && <Typography sx={{ fontSize: '0.72rem', color: COLORS.primary, fontFamily: PP }}>{kwpHint}</Typography>}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Label>Inverter Size (kW)</Label>
                                    <TextField {...fieldSx} type="number" placeholder="auto" step="0.1" value={formData.kw} onChange={e => set('kw', e.target.value)} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Label>Battery (kWh)</Label>
                                    <TextField {...fieldSx} type="number" placeholder="0" step="0.1" value={formData.battKwh} onChange={e => set('battKwh', e.target.value)} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Tariff */}
                    <Box>
                        <SectionTitle>⚡ Tariff</SectionTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box>
                                <Label required>Tariff Type</Label>
                                <FormControl size="small" fullWidth>
                                    <Select value={formData.tariffType} onChange={e => set('tariffType', e.target.value)} displayEmpty
                                        sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' }}>
                                        <MenuItem value="" sx={{ fontFamily: PP, color: COLORS.disabled }}>— Select Tariff —</MenuItem>
                                        <MenuItem value="0.515" sx={{ fontFamily: PP }}>Low Voltage (RM 0.515/kWh)</MenuItem>
                                        <MenuItem value="0.303" sx={{ fontFamily: PP }}>Medium Voltage (RM 0.303/kWh)</MenuItem>
                                        <MenuItem value="0.437" sx={{ fontFamily: PP }}>High Voltage (RM 0.437/kWh)</MenuItem>
                                        <MenuItem value="0.451" sx={{ fontFamily: PP }}>Agriculture (RM 0.451/kWh)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box>
                                <Label>Monthly TNB Bill (RM) — optional</Label>
                                <RMField value={formData.monthlyBill} onChange={v => set('monthlyBill', v)} placeholder="5,000" />
                                {billHint && <Typography sx={{ fontSize: '0.72rem', color: COLORS.success, fontFamily: PP, mt: 0.5 }}>{billHint}</Typography>}
                            </Box>
                        </Box>
                    </Box>

                    {/* Pricing */}
                    <Box>
                        <SectionTitle>💵 Pricing</SectionTitle>
                        <ModeToggle value={priceMode} onChange={handlePriceModeChange} options={[['kwp','Per kWp'],['total','Total Price']]} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {priceMode === 'kwp' ? (
                                <Box><Label>Price per kWp (RM)</Label><RMField value={formData.pricePerKwp} onChange={v => set('pricePerKwp', v)} placeholder="3,500" /></Box>
                            ) : (
                                <Box><Label>Total Solar Price (RM)</Label><RMField value={formData.totalSolarPrice} onChange={v => set('totalSolarPrice', v)} placeholder="100,000" /></Box>
                            )}
                            {residentialAutoFilled && (
                                <Typography sx={{ fontSize: '0.7rem', color: COLORS.success, fontFamily: PP, mt: -0.5 }}>
                                    ✅ Auto-filled from Residential Pricing — {phaseMode === '3phase' ? '3-phase' : 'single-phase'}, {(formData.inverterBrand || 'goodwe') === 'huawei' ? 'Huawei' : 'GoodWe'} (resi as-is).
                                </Typography>
                            )}
                            {!residentialAutoFilled && parseFloat(formData.kwp) >= 26 && formData.pricePerKwp && (
                                <Typography sx={{ fontSize: '0.7rem', color: COLORS.success, fontFamily: PP, mt: -0.5 }}>
                                    ✅ Auto-filled from Commercial Pricing — {(formData.inverterBrand || 'goodwe').toUpperCase()} {parseFloat(formData.kwp) <= 30 ? '26–30' : parseFloat(formData.kwp) <= 100 ? '31–100' : parseFloat(formData.kwp) <= 400 ? '101–400' : '>400'} kWp tier.
                                </Typography>
                            )}
                            <Box>
                                <Label>Add-on Cost (RM) <span style={{ color: COLORS.sub, fontWeight: 400 }}>(auto from Battery kWh)</span></Label>
                                <RMField value={formData.battCost} onChange={v => { set('battCost', v); setBattCostTouched(true); }} placeholder="0" />
                                {formData.battKwh && Number(formData.battKwh) > 0 && (
                                    <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                                        Tier: RM {batteryPricePerKwh(Number(formData.battKwh))}/kWh × {formData.battKwh} kWh
                                        {' '}({Number(formData.battKwh) < 100 ? '<100' : Number(formData.battKwh) <= 1000 ? '100-1000' : '>1000'} kWh band)
                                    </Typography>
                                )}
                            </Box>
                            {showBreakdown && (
                                <Box sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px', p: 1.5, border: `1px solid ${COLORS.border}` }}>
                                    <BRow label="Solar System" value={breakdown.solar} />
                                    <BRow label="Add-on"       value={breakdown.battery} />
                                    <Box sx={{ borderTop: `1px solid ${COLORS.border}`, my: 0.5 }} />
                                    <BRow label="Capital Cost" value={breakdown.total}    color={COLORS.primary} />
                                    <Box sx={{ borderTop: `1px solid ${COLORS.border}`, my: 0.5 }} />
                                    <BRow label="Maintenance/yr" value={breakdown.maint}    color={COLORS.success} />
                                    <BRow label="Tax Relief"     value={breakdown.tax}      color="#8B5CF6" />
                                    <BRow label="After-Tax Cost" value={breakdown.afterTax} color="#8B5CF6" />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Discount */}
                    <Box>
                        <SectionTitle>🏷️ Discount (Optional)</SectionTitle>
                        <ModeToggle value={discMode} onChange={setDiscMode} options={[['none','None'],['pct','By %'],['amt','By Amount']]} />
                        {discMode === 'pct' && <Box><Label>Discount on Capital Cost (%)</Label><PctField value={formData.discPct} onChange={v => set('discPct', v)} placeholder="10" /></Box>}
                        {discMode === 'amt' && <Box><Label>Discount Amount (RM)</Label><RMField value={formData.discAmt} onChange={v => set('discAmt', v)} placeholder="5,000" /></Box>}
                    </Box>

                    {/* Saving Reduction */}
                    <Box>
                        <SectionTitle>📉 Saving Reduction (Optional)</SectionTitle>
                        <ModeToggle value={savingMode} onChange={setSavingMode} options={[['none','None'],['pct','By %'],['amt','By Amount']]} />
                        {savingMode === 'pct' && <Box><Label>Reduce Monthly Saving by (%)</Label><PctField value={formData.savingRedPct} onChange={v => set('savingRedPct', v)} placeholder="10" /></Box>}
                        {savingMode === 'amt' && <Box><Label>Reduce Monthly Saving by (RM)</Label><RMField value={formData.savingRedAmt} onChange={v => set('savingRedAmt', v)} placeholder="500" /></Box>}
                    </Box>

                    {/* Generation Mode */}
                    <Box>
                        <SectionTitle>⚡ Generation Mode</SectionTitle>
                        <ModeToggle value={genMode} onChange={handleGenModeChange} options={[['std','Standard'],['sare','SARE']]} />
                        {genMode === 'sare' && (
                            <Box>
                                <Label>SARE Rate (RM/kWh)</Label>
                                <RMField value={formData.sareRate} onChange={v => set('sareRate', v)} placeholder="0.36" />
                                <Typography sx={{ fontSize: '0.72rem', color: COLORS.primary, fontFamily: PP, mt: 0.5 }}>
                                    Saving = kWp × 3.4 × 30 × (Tariff − SARE Rate)
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Calculate */}
                    <Button fullWidth onClick={calculate} sx={{
                        background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white',
                        borderRadius: '8px', fontFamily: PP, fontWeight: 700, fontSize: '0.9rem',
                        textTransform: 'none', py: 1.2, boxShadow: 'none', mt: 1,
                        '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: '0 4px 12px rgba(26,140,255,0.35)' },
                    }}>⚡ Calculate ROI</Button>
                </Box>

                {/* ── Right Panel ── */}
                <Box sx={{ flex: 1, p: { xs: 1.5, md: 2.5 }, minWidth: 0 }}>
                    {!calcResult ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                            <Typography sx={{ fontSize: '3rem', mb: 1 }}>📊</Typography>
                            <Typography sx={{ fontSize: '0.875rem', fontFamily: PP, textAlign: 'center', color: COLORS.sub }}>
                                Fill in the system specs on the left<br />and click <strong>Calculate ROI</strong>
                            </Typography>
                        </Box>
                    ) : (
                        <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', borderTop: `4px solid ${COLORS.success}` }}>
                            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#F8FAFC' }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.text, fontFamily: PP }}>💬 WhatsApp Proposal</Typography>
                                <Chip label={currentLang === 'en' ? 'EN 🇬🇧' : currentLang === 'zh' ? '中文 🇨🇳' : 'BM 🇲🇾'}
                                    size="small" sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', borderRadius: 6, backgroundColor: COLORS.light, color: COLORS.dark }} />
                            </Box>
                            <Box sx={{ m: 2, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                                <Box sx={{ backgroundColor: '#075e54', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ width: 36, height: 36, backgroundColor: '#128c7e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>☀️</Box>
                                    <Box>
                                        <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem', fontFamily: PP }}>Solar Sales</Typography>
                                        <Typography sx={{ color: '#80cbc4', fontSize: '0.75rem', fontFamily: PP }}>WhatsApp Business</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ backgroundColor: '#e5ddd5', minHeight: 200, maxHeight: { xs: 400, md: 'none' }, overflowY: 'auto', WebkitOverflowScrolling: 'touch', p: 1.5 }}>
                                    <Box sx={{ backgroundColor: 'white', borderRadius: '0 8px 8px 8px', p: 1.5, maxWidth: { xs: '100%', md: '90%' }, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <pre style={{ fontFamily: "'Courier New', monospace", fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4, color: COLORS.text, margin: 0 }}>
                                            {buildCscWhatsAppMsg(calcResult, currentLang)}
                                        </pre>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                                <Button onClick={copyMsg} variant="outlined" size="small" sx={{
                                    fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', boxShadow: 'none',
                                    borderColor: isCopied ? COLORS.success : COLORS.border,
                                    color: isCopied ? COLORS.success : COLORS.text,
                                    backgroundColor: isCopied ? '#D1FAE5' : 'white',
                                }}>{copyText}</Button>
                                <Button onClick={openWA} variant="contained" size="small" sx={{
                                    fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem',
                                    backgroundColor: COLORS.success, boxShadow: 'none', '&:hover': { backgroundColor: '#059669' },
                                }}>📲 Send WhatsApp</Button>
                                <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, ml: { xs: 0, md: 'auto' }, display: { xs: 'none', md: 'block' } }}>
                                    Desktop: auto-copies &amp; opens WhatsApp Web
                                </Typography>
                            </Box>
                        </Card>
                    )}
                </Box>
            </Box>
            )}
            <ToolDescription configKey="CscDescription" />
        </Box>
    );
}
